import { useState } from 'react';
import type { FormField } from '@/types/form';
import { fieldTemplates, formTemplates, createFieldFromTemplate, createFormFromTemplate } from '@/utils/form-templates';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Plus, Files } from 'lucide-react';

interface FieldPaletteProps {
  onAddField: (field: Omit<FormField, 'id' | 'sortOrder'>) => void;
}

export const FieldPalette: React.FC<FieldPaletteProps> = ({ onAddField }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'fields' | 'templates'>('fields');

  const filteredFieldTemplates = fieldTemplates.filter(template =>
    template.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFormTemplates = formTemplates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddFieldFromTemplate = (templateType: string) => {
    const template = fieldTemplates.find(t => t.type === templateType);
    if (template) {
      const field = createFieldFromTemplate(template);
      onAddField(field);
    }
  };

  const handleAddFromFormTemplate = (templateId: string) => {
    try {
      const fields = createFormFromTemplate(templateId);
      fields.forEach(field => onAddField(field));
    } catch (error) {
      console.error('Failed to add template:', error);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-border">
        <div className="flex gap-1 mb-4">
          <Button
            variant={activeTab === 'fields' ? 'default' : 'outline'}
            size="sm"
            className="flex-1"
            onClick={() => setActiveTab('fields')}
          >
            <Plus className="h-3 w-3 mr-1" />
            Fields
          </Button>
          <Button
            variant={activeTab === 'templates' ? 'default' : 'outline'}
            size="sm"
            className="flex-1"
            onClick={() => setActiveTab('templates')}
          >
            <Files className="h-3 w-3 mr-1" />
            Templates
          </Button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={`Search ${activeTab}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'fields' ? (
          <div className="space-y-2">
            {filteredFieldTemplates.map((template) => (
              <Card
                key={template.type}
                className="cursor-pointer hover:bg-accent transition-colors"
                onClick={() => handleAddFieldFromTemplate(template.type)}
              >
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">{template.icon}</div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-sm mb-1">{template.label}</h3>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {template.description}
                      </p>
                      <Badge variant="secondary" className="mt-2 text-xs">
                        {template.type}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {filteredFieldTemplates.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No fields found</p>
                <p className="text-xs">Try adjusting your search</p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
              Form Templates
            </div>
            
            {filteredFormTemplates.map((template) => (
              <Card
                key={template.id}
                className="cursor-pointer hover:bg-accent transition-colors"
                onClick={() => handleAddFromFormTemplate(template.id)}
              >
                <CardHeader className="p-3 pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-sm font-medium mb-1">
                        {template.name}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {template.description}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs">
                      {template.category}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {template.fields.length} fields
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}

            {filteredFormTemplates.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Files className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No templates found</p>
                <p className="text-xs">Try adjusting your search</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="p-4 border-t border-border">
        <div className="space-y-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start"
            onClick={() => {
              const field = createFieldFromTemplate(fieldTemplates[0]);
              onAddField({ ...field, name: 'quick_text', label: 'Quick Text Field' });
            }}
          >
            <Plus className="h-3 w-3 mr-2" />
            Quick Text Field
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start"
            onClick={() => {
              const field = createFieldFromTemplate(fieldTemplates.find(t => t.type === 'select')!);
              onAddField({ ...field, name: 'quick_dropdown', label: 'Quick Dropdown' });
            }}
          >
            <Plus className="h-3 w-3 mr-2" />
            Quick Dropdown
          </Button>
        </div>
      </div>
    </div>
  );
};